import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "./EmailField.scss";
import { isUrl } from "../../../utils/generalUtils";

const EmailField = ({
  data,
  formValidation = true,
  formClass,
  formatters = {},
  validators = {},
  onChange,
}) => {
  const [value, setValue] = useState(""); // Original value (what gets saved)
  const [displayValue, setDisplayValue] = useState(""); // Display value (what user sees)
  const [isValid, setIsValid] = useState(formValidation);
  const [errorMessage, setErrorMessage] = useState("");
  const [multipleValues, setMultipleValues] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState("");

  const inputRef = useRef(null);
  const editInputRef = useRef(null);

  // Memoized initial value calculation
  const initialValue = useMemo(() => {
    if (data?.answer?.value) {
      return data.answer.value;
    }
    if (data?.defaultValue) {
      return data.defaultValue;
    }
    return "";
  }, [data?.key, data?.answer?.value, data?.defaultValue]);

  // Enhanced input mask application with multiple mask support
  const applyInputMask = useCallback((inputValue, mask) => {
    if (!mask) return inputValue;

    // Handle multiple masks
    if (Array.isArray(mask)) {
      for (const singleMask of mask) {
        const masked = applyInputMask(inputValue, singleMask);
        if (masked.length === singleMask.length) {
          return masked;
        }
      }
      return inputValue;
    }

    let maskedValue = "";
    let valueIndex = 0;
    const cleanValue = inputValue;

    for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
      const maskChar = mask[i];
      const inputChar = cleanValue[valueIndex];

      if (maskChar === "9" && /\d/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "a" && /[a-zA-Z]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "*" && /[a-zA-Z0-9]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "A" && /[A-Z]/.test(inputChar.toUpperCase())) {
        maskedValue += inputChar.toUpperCase();
        valueIndex++;
      } else if (
        maskChar !== "9" &&
        maskChar !== "a" &&
        maskChar !== "*" &&
        maskChar !== "A"
      ) {
        maskedValue += maskChar;
        if (inputChar === maskChar) {
          valueIndex++;
        }
      } else {
        valueIndex++;
        i--;
      }
    }

    return maskedValue;
  }, []);

  // Helper function to check if character is valid for mask position
  const isValidMaskChar = useCallback((char, maskChar) => {
    switch (maskChar) {
      case "9":
        return /\d/.test(char);
      case "a":
        return /[a-zA-Z]/.test(char);
      case "*":
        return /[a-zA-Z0-9]/.test(char);
      case "A":
        return /[A-Z]/.test(char.toUpperCase());
      default:
        return char === maskChar;
    }
  }, []);

  // Extract clean value from masked value
  const extractCleanValue = useCallback((maskedValue, mask) => {
    if (!mask) return maskedValue;

    // Handle multiple masks - try to find which mask matches
    if (Array.isArray(mask)) {
      for (const singleMask of mask) {
        const cleanValue = extractCleanValue(maskedValue, singleMask);
        // Check if this mask could produce this masked value
        const reapplied = applyDisplayMask(cleanValue, singleMask);
        if (reapplied === maskedValue) {
          return cleanValue;
        }
      }
      // If no mask matches perfectly, extract from the first mask
      return extractCleanValue(maskedValue, mask[0]);
    }

    let cleanValue = "";
    let maskIndex = 0;

    for (let i = 0; i < maskedValue.length && maskIndex < mask.length; i++) {
      const char = maskedValue[i];
      const maskChar = mask[maskIndex];

      if (
        maskChar === "9" ||
        maskChar === "a" ||
        maskChar === "*" ||
        maskChar === "A"
      ) {
        // This is a data character
        cleanValue += char;
        maskIndex++;
      } else {
        // This is a mask separator character
        if (char === maskChar) {
          maskIndex++;
        } else {
          // Character doesn't match expected mask separator
          break;
        }
      }
    }

    return cleanValue;
  }, []);

  // Apply display mask - only affects display, doesn't change original value
  const applyDisplayMask = useCallback((inputValue, displayMask) => {
    if (!displayMask) return inputValue;

    // Handle multiple masks
    if (Array.isArray(displayMask)) {
      for (const singleMask of displayMask) {
        const masked = applyDisplayMask(inputValue, singleMask);
        // Check if we can fit the entire input into this mask
        if (inputValue.length <= singleMask.replace(/[^9a*A]/g, "").length) {
          return masked;
        }
      }
      // If no mask fits perfectly, use the first one
      return applyDisplayMask(inputValue, displayMask[0]);
    }

    let maskedValue = "";
    let valueIndex = 0;

    for (
      let i = 0;
      i < displayMask.length && valueIndex < inputValue.length;
      i++
    ) {
      const maskChar = displayMask[i];
      const inputChar = inputValue[valueIndex];

      if (maskChar === "9" && /\d/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "a" && /[a-zA-Z]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "*" && /[a-zA-Z0-9]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "A" && /[A-Z]/.test(inputChar.toUpperCase())) {
        maskedValue += inputChar.toUpperCase();
        valueIndex++;
      } else if (
        maskChar !== "9" &&
        maskChar !== "a" &&
        maskChar !== "*" &&
        maskChar !== "A"
      ) {
        maskedValue += maskChar;
        // Don't increment valueIndex for mask characters
      } else {
        break; // Stop if character doesn't match mask
      }
    }

    return maskedValue;
  }, []);

  // Validate input against display mask
  const isValidForDisplayMask = useCallback(
    (newChar, currentValue, displayMask) => {
      if (!displayMask) return true;

      // Handle multiple masks
      if (Array.isArray(displayMask)) {
        return displayMask.some((mask) =>
          isValidForDisplayMask(newChar, currentValue, mask)
        );
      }

      // Find the next expected mask character position
      const currentDisplayValue = applyDisplayMask(currentValue, displayMask);
      const nextMaskPosition = currentDisplayValue.length;

      if (nextMaskPosition >= displayMask.length) {
        return false; // Mask is full
      }

      const nextMaskChar = displayMask[nextMaskPosition];

      // If it's a literal character, check if we need to auto-insert it
      if (
        nextMaskChar !== "9" &&
        nextMaskChar !== "a" &&
        nextMaskChar !== "*" &&
        nextMaskChar !== "A"
      ) {
        // This is a separator character, check if the input matches the next data position
        let dataPosition = nextMaskPosition + 1;
        while (dataPosition < displayMask.length) {
          const nextDataChar = displayMask[dataPosition];
          if (
            nextDataChar === "9" ||
            nextDataChar === "a" ||
            nextDataChar === "*" ||
            nextDataChar === "A"
          ) {
            return isValidMaskChar(newChar, nextDataChar);
          }
          dataPosition++;
        }
        return false;
      }

      return isValidMaskChar(newChar, nextMaskChar);
    },
    [isValidMaskChar, applyDisplayMask]
  );

  // Generic formatter application
  const applyFormatter = useCallback(
    (inputValue, formatterKey) => {
      const formatter = formatters[formatterKey];
      if (formatter && typeof formatter === "function") {
        return formatter(inputValue);
      }

      // Built-in formatters
      switch (formatterKey) {
        case "uppercase":
          return inputValue.toUpperCase();
        case "lowercase":
          return inputValue.toLowerCase();
        case "capitalize":
          return (
            inputValue.charAt(0).toUpperCase() +
            inputValue.slice(1).toLowerCase()
          );
        case "trim":
          return inputValue.trim();
        default:
          return inputValue;
      }
    },
    [formatters]
  );

  // Enhanced email validation function
  const isValidEmail = useCallback((email) => {
    // Basic email regex pattern
    const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    // More comprehensive email validation
    const comprehensiveEmailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!basicEmailRegex.test(email)) {
      return false;
    }
    
    // Additional checks
    if (email.length > 254) return false; // RFC 5321 limit
    
    const [localPart, domain] = email.split('@');
    
    // Local part checks
    if (localPart.length > 64) return false; // RFC 5321 limit
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
    if (localPart.includes('..')) return false;
    
    // Domain checks
    if (domain.length > 253) return false;
    if (domain.startsWith('-') || domain.endsWith('-')) return false;
    if (domain.includes('..')) return false;
    
    return comprehensiveEmailRegex.test(email);
  }, []);

  // Generate dynamic error messages
  const generateErrorMessage = useCallback(
    (validationType, config, inputValue) => {
      const validation = data?.validate || {};

      // Return custom message if provided
      if (validation.customMessage) {
        return validation.customMessage;
      }

      // Generate dynamic messages based on validation type
      switch (validationType) {
        case "required":
          return data?.invalidMessage || "Email address is required";
        case "minLength":
          return `Email must be at least ${config} characters (current: ${inputValue.length})`;
        case "maxLength":
          return `Email must be no more than ${config} characters (current: ${inputValue.length})`;
        case "pattern":
          return "Email format doesn't match the required pattern";
        case "email":
          return "Please enter a valid email address (e.g., user@example.com)";
        case "custom":
          return "Invalid email address";
        default:
          return "Invalid email address";
      }
    },
    [data?.validate, data?.invalidMessage]
  );

  // Enhanced validation with email-specific checks
  const validateInput = useCallback(
    (inputValue) => {
      const validation = data?.validate || {};
      const customValidator = validators[data?.key];

      // Custom validator takes precedence
      if (customValidator && typeof customValidator === "function") {
        return customValidator(inputValue, validation);
      }

      let isValid = true;
      let errorMessage = "";

      if (validation?.required && !inputValue.trim()) {
        isValid = false;
        errorMessage = generateErrorMessage("required", null, inputValue);
      } else if (inputValue.trim()) {
        // Length validation
        if (validation?.minLength && inputValue.length < validation.minLength) {
          isValid = false;
          errorMessage = generateErrorMessage(
            "minLength",
            validation.minLength,
            inputValue
          );
        } else if (
          validation?.maxLength &&
          inputValue.length > validation.maxLength
        ) {
          isValid = false;
          errorMessage = generateErrorMessage(
            "maxLength",
            validation.maxLength,
            inputValue
          );
        } else {
          // Pattern validation (if custom pattern is provided)
          if (
            validation?.pattern &&
            !new RegExp(validation.pattern).test(inputValue)
          ) {
            isValid = false;
            errorMessage = generateErrorMessage(
              "pattern",
              validation.pattern,
              inputValue
            );
          } else {
            // Email validation - this is the key difference from TextField
            if (!isValidEmail(inputValue)) {
              isValid = false;
              errorMessage = generateErrorMessage("email", null, inputValue);
            }
          }
        }
      }

      return { isValid, errorMessage };
    },
    [
      data?.validate,
      data?.invalidMessage,
      data?.key,
      validators,
      generateErrorMessage,
      isValidEmail,
    ]
  );

  // Label positioning utilities
  const labelStyles = useMemo(() => {
    const position = data?.labelPosition || "top";
    const isHorizontal = [
      "left-left",
      "left-right",
      "right-left",
      "right-right",
    ].includes(position);

    const containerStyle = {
      top: {
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
      },
      bottom: {
        flexDirection: "column-reverse",
        justifyContent: "flex-start",
        alignItems: "stretch",
      },
      "left-left": {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
      },
      "left-right": {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
      },
      "right-left": {
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
        alignItems: "center",
      },
      "right-right": {
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
        alignItems: "center",
      },
    }[position] || {
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "stretch",
    };

    const labelStyle = {
      width: isHorizontal && data?.labelWidth ? `${data.labelWidth}%` : "auto",
      textAlign: ["left-right", "right-right"].includes(position)
        ? "right"
        : "left",
      marginRight:
        ["left-left", "left-right"].includes(position) && data?.labelMargin
          ? `${data.labelMargin}%`
          : "0",
      marginLeft:
        ["right-left", "right-right"].includes(position) && data?.labelMargin
          ? `${data.labelMargin}%`
          : "0",
      paddingTop: ["top", "bottom"].includes(position) ? "12px" : "0",
    };

    return { containerStyle, labelStyle };
  }, [data?.labelPosition, data?.labelWidth, data?.labelMargin]);

  // Handle multiple values for multiple email inputs
  const handleMultipleAdd = useCallback(() => {
    if (value.trim()) {
      // Validate the current value before adding
      const validation = validateInput(value);
      if (validation.isValid) {
        const newValues = [...multipleValues, value.trim().toLowerCase()];
        setMultipleValues(newValues);
        setValue("");
        setDisplayValue("");
        setIsValid(true);
        setErrorMessage("");
        // Call onChange with array of values for multiple input
        onChange?.(newValues);
      } else {
        // Show validation error
        setIsValid(false);
        setErrorMessage(validation.errorMessage);
      }
    }
  }, [value, multipleValues, onChange, validateInput]);

  const handleMultipleRemove = useCallback(
    (index) => {
      if (
        multipleValues.length > 0 &&
        index >= 0 &&
        index < multipleValues.length
      ) {
        const newValues = multipleValues.filter((_, i) => i !== index);
        setMultipleValues(newValues);
        // Call onChange with updated array
        onChange?.(newValues);
      }
    },
    [multipleValues, onChange]
  );

  // Handle editing multiple values
  const handleMultipleEdit = useCallback(
    (index) => {
      if (index >= 0 && index < multipleValues.length) {
        setEditingIndex(index);
        setEditingValue(multipleValues[index]);
      }
    },
    [multipleValues]
  );

  const handleMultipleSave = useCallback(
    (index) => {
      if (editingValue.trim() && index >= 0 && index < multipleValues.length) {
        // Validate the edited value
        const validation = validateInput(editingValue.trim());
        if (validation.isValid) {
          const newValues = [...multipleValues];
          newValues[index] = editingValue.trim().toLowerCase();
          setMultipleValues(newValues);
          setEditingIndex(-1);
          setEditingValue("");
          onChange?.(newValues);
        }
      }
    },
    [editingValue, multipleValues, onChange, validateInput]
  );

  const handleMultipleCancel = useCallback(() => {
    setEditingIndex(-1);
    setEditingValue("");
  }, []);

  // Event handlers
  const handleFocus = useCallback((e) => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(
    (e) => {
      setIsFocused(false);

      let finalValue = data?.displayMask ? value : e.target.value;

      // Apply input mask on blur if specified
      if (data?.inputMask && data?.applyMaskOn === "blur") {
        finalValue = applyInputMask(finalValue, data.inputMask);
        setValue(finalValue);
        setDisplayValue(finalValue);
      }
      // Apply display mask on blur if specified
      else if (data?.displayMask && data?.applyMaskOn === "blur") {
        const displayMaskedValue = applyDisplayMask(
          finalValue,
          data.displayMask
        );
        setDisplayValue(displayMaskedValue);
        // Keep original value clean for saving
        setValue(finalValue);
      }

      // Apply formatter on blur
      if (data?.formatter && data?.applyFormatterOn === "blur") {
        finalValue = applyFormatter(finalValue, data.formatter);
        setValue(finalValue);
        if (!data?.displayMask) {
          setDisplayValue(finalValue);
        } else {
          setDisplayValue(applyDisplayMask(finalValue, data.displayMask));
        }
      }

      // Always convert to lowercase for email (unless case formatter is specified)
      if (!data?.formatter || data?.formatter !== "uppercase") {
        finalValue = finalValue.toLowerCase();
        setValue(finalValue);
        if (!data?.displayMask) {
          setDisplayValue(finalValue);
        }
      }

      // Validate and only call onChange if valid (unless multiple input)
      if (!data?.multiple) {
        const validation = validateInput(finalValue);
        setIsValid(validation.isValid);
        setErrorMessage(validation.errorMessage);

        // Only call onChange if validation passes
        if (validation.isValid && finalValue !== value) {
          onChange?.(finalValue);
        }
      }
    },
    [
      data,
      applyInputMask,
      applyDisplayMask,
      applyFormatter,
      onChange,
      value,
      validateInput,
    ]
  );

  const handleChange = useCallback(
    (e) => {
      let inputValue = e.target.value;
      let finalCleanValue = inputValue; // This will be the actual value to save
      let finalDisplayValue = inputValue; // This will be what user sees

      // Handle input vs display mask
      if (data?.inputMask && data?.applyMaskOn === "change") {
        // Input mask affects both original and display value
        const maskedValue = applyInputMask(inputValue, data.inputMask);
        finalCleanValue = maskedValue;
        finalDisplayValue = maskedValue;
      } else if (data?.displayMask && data?.applyMaskOn === "change") {
        // For display mask, we need to handle character validation
        const currentCleanValue = value;
        const currentDisplayValue = displayValue;

        // Check if this is a deletion
        if (inputValue.length < currentDisplayValue.length) {
          // Handle deletion - extract clean value from the new display value
          finalCleanValue = extractCleanValue(inputValue, data.displayMask);
          finalDisplayValue = inputValue;
        } else if (inputValue.length > currentDisplayValue.length) {
          // Handle addition - validate new character
          const newChar = inputValue.charAt(inputValue.length - 1);
          if (isValidForDisplayMask(newChar, currentCleanValue, data.displayMask)) {
            finalCleanValue = currentCleanValue + newChar;
            finalDisplayValue = applyDisplayMask(finalCleanValue, data.displayMask);
          } else {
            // If character is not valid, don't update anything - return early
            return;
          }
        } else {
          // Same length, might be a replacement
          finalCleanValue = extractCleanValue(inputValue, data.displayMask);
          finalDisplayValue = applyDisplayMask(finalCleanValue, data.displayMask);
        }
      } else {
        // No mask, use input value directly
        finalCleanValue = inputValue;
        finalDisplayValue = inputValue;
      }

      // Apply custom formatter (use the clean value for formatting)
      if (data?.formatter && data?.applyFormatterOn === "change") {
        finalCleanValue = applyFormatter(finalCleanValue, data.formatter);
        if (!data?.displayMask && !data?.inputMask) {
          finalDisplayValue = finalCleanValue;
        } else if (data?.displayMask) {
          finalDisplayValue = applyDisplayMask(finalCleanValue, data.displayMask);
        }
      }

      // Apply length restrictions (on clean value)
      if (
        data?.validate?.maxLength &&
        finalCleanValue.length > data.validate.maxLength
      ) {
        finalCleanValue = finalCleanValue.substring(0, data.validate.maxLength);
        if (data?.displayMask) {
          finalDisplayValue = applyDisplayMask(finalCleanValue, data.displayMask);
        } else {
          finalDisplayValue = finalCleanValue;
        }
      }

      // Update state with final values
      setValue(finalCleanValue);
      setDisplayValue(finalDisplayValue);

      // Validate input (use final clean value) — only show format errors on blur, not while typing
      if (!data?.validateWhenHidden || !data?.hidden) {
        if (!finalCleanValue.trim()) {
          // Empty field: clear any format errors (required error shows on blur/submit)
          setIsValid(true);
          setErrorMessage("");
        } else {
          // Non-empty: only flag required errors while typing, not format errors
          setIsValid(true);
          setErrorMessage("");
        }

        // For non-multiple inputs, call onChange with the final clean value
        if (!data?.multiple) {
          onChange?.(finalCleanValue);
        }
      } else if (!data?.multiple) {
        // Even if validation is skipped, still call onChange with clean value
        onChange?.(finalCleanValue);
      }
    },
    [
      data,
      applyInputMask,
      applyDisplayMask,
      applyFormatter,
      validateInput,
      onChange,
      value,
      displayValue,
      isValidForDisplayMask,
      extractCleanValue,
    ]
  );

  // Handle Enter key for multiple inputs
  const handleKeyDown = useCallback(
    (e) => {
      if (data?.multiple && e.key === "Enter") {
        e.preventDefault();
        handleMultipleAdd();
      }
    },
    [data?.multiple, handleMultipleAdd]
  );

  // Initialize component
  useEffect(() => {
    setIsValid(formValidation);

    if (data?.multiple && Array.isArray(initialValue)) {
      setMultipleValues(initialValue);
      setValue("");
      setDisplayValue("");
    } else {
      setValue(initialValue);
      // If display mask is present, apply it to initial value
      if (data?.displayMask) {
        setDisplayValue(applyDisplayMask(initialValue, data.displayMask));
      } else {
        setDisplayValue(initialValue);
      }
    }

    if (data?.autofocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [
    initialValue,
    formValidation,
    data?.autofocus,
    data?.multiple,
    data?.displayMask,
    applyDisplayMask,
  ]);

  // Focus edit input when editing starts
  useEffect(() => {
    if (editingIndex !== -1 && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingIndex]);

  // Don't render if hidden
  if (data?.hidden) {
    return null;
  }

  // Render label
  const renderLabel = () => {
    if (data?.hideLabel || !data?.label) return null;

    return (
      <div className="emailfield__label" style={labelStyles.labelStyle}>
        {data?.labelPrefix && (
          <span className="emailfield__label-prefix">{data.labelPrefix}</span>
        )}
        {data.label}
        {data?.validate?.required && (
          <span
            className={`emailfield__required ${
              !isValid ? "emailfield__required--error" : ""
            }`}
          >
            {" "}
            *
          </span>
        )}
        {data?.tooltip && (
          <span className="emailfield__tooltip" title={data.tooltip}>
            ℹ️
          </span>
        )}
      </div>
    );
  };

  // Render multiple values display with improved editing UI
  const renderMultipleValues = () => {
    if (!data?.multiple || multipleValues.length === 0) return null;

    return (
      <div className="emailfield__multiple-values">
        {multipleValues.map((val, index) => (
          <div key={index} className="emailfield__multiple-tag">
            {editingIndex === index ? (
              <div className="emailfield__multiple-edit">
                <input
                  ref={editInputRef}
                  type="email"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className="emailfield__multiple-edit-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleMultipleSave(index);
                    } else if (e.key === "Escape") {
                      handleMultipleCancel();
                    }
                  }}
                />
                <div className="emailfield__multiple-edit-actions">
                  <button
                    type="button"
                    onClick={() => handleMultipleSave(index)}
                    className="emailfield__multiple-save"
                    aria-label="Save changes"
                    title="Save changes"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleMultipleCancel}
                    className="emailfield__multiple-cancel"
                    aria-label="Cancel editing"
                    title="Cancel editing"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span
                  className="emailfield__multiple-text"
                  onClick={() => handleMultipleEdit(index)}
                  title="Click to edit"
                >
                  {val}
                </span>
                <button
                  type="button"
                  onClick={() => handleMultipleRemove(index)}
                  className="emailfield__multiple-remove"
                  aria-label={`Remove ${val}`}
                  title={`Remove ${val}`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render input with proper email-specific features
  const renderInput = () => {
    // Determine placeholder - prioritize inputMaskPlaceholder over regular placeholder
    let placeholder = data?.placeholder || "Enter email address";
    if (
      (data?.inputMask || data?.displayMask) &&
      data?.inputMaskPlaceholderChar
    ) {
      placeholder = data?.inputMaskPlaceholderChar;
    }

    const inputProps = {
      ref: inputRef,
      type: "email",
      disabled: data?.disabled || false,
      readOnly: data?.readOnly || false,
      value: displayValue,
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      placeholder: placeholder,
      className: `emailfield__input ${data?.customClass || ""}`,
      maxLength: data?.validate?.maxLength,
      minLength: data?.validate?.minLength,
      autoComplete: data?.autocomplete || "email",
      spellCheck: data?.spellcheck !== false,
      "aria-invalid": !isValid,
      "aria-describedby": !isValid ? `${data?.key}-error` : undefined,
      "data-input-mask": data?.inputMask,
      "data-display-mask": data?.displayMask,
    };

    const inputWrapperClasses = [
      "emailfield__input-wrapper my-2",
      isFocused && "emailfield__input-wrapper--focused",
      !isValid && "emailfield__input-wrapper--error",
      data?.disabled && "emailfield__input-wrapper--disabled",
      data?.readOnly && "emailfield__input-wrapper--readonly",
      (data?.prefix || data?.suffix) && "emailfield__input-wrapper--with-addons",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="emailfield__field">
        {renderMultipleValues()}

        <div className={inputWrapperClasses}>
          {/* Prefix */}
          {data?.prefix && (
            <span className="emailfield__prefix">{data.prefix}</span>
          )}

          {/* Email Input */}
          <input {...inputProps} />

          {/* Suffix */}
          {data?.suffix && (
            <span className="emailfield__suffix">{data.suffix}</span>
          )}
        </div>

        {/* Multiple input add button */}
        {data?.multiple && (
          <button
            type="button"
            onClick={handleMultipleAdd}
            className="emailfield__multiple-add"
            disabled={!value.trim()}
          >
            Add Email
          </button>
        )}

        {/* Error Messages */}
        {!isValid && (
          <div id={`${data?.key}-error`} className="emailfield__error">
            {errorMessage}
          </div>
        )}

        {/* Character Count for email */}
        {data?.showCharCount && !data?.multiple && (
          <div className="emailfield__counts">
            <span className="emailfield__count">
              Characters: {displayValue.length}
              {data?.validate?.maxLength && `/${data.validate.maxLength}`}
            </span>
          </div>
        )}

        {/* Description */}
        {data?.description && (
          <div className="emailfield__description">
            {isUrl(data.description) ? (
              <img
                src={data.description}
                alt="Field description"
                className="emailfield__description-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentNode.textContent = data.description;
                }}
              />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: data.description }} />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`emailfield ${formClass || ""} ${
        data?.disabled ? "emailfield--disabled" : ""
      } ${data?.readOnly ? "emailfield--readonly" : ""}`}
    >
      <div className="emailfield__container" style={labelStyles.containerStyle}>
        {renderLabel()}
        {renderInput()}
      </div>
    </div>
  );
};

export default EmailField;